import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import getTicketsPagePath from '../../../library/getTicketsPagePath';
import {getEventTickets} from '../../../redux/tickets/ticketsActions';
import {getPlans, setCurrentPlan} from '../../../redux/subscription/subscriptionActions';
import type {AiAgentExecuteResult} from '../../../redux/aiAgent/aiAgent.type';
import {dispatchCustomEvent} from '../../../hooks/dispatchEvent';
import {resetAgentEventState} from './resetAgentEventState';
import {closeAgentPanel, isMobileViewport} from './agentPanelState';

export type NavigateFn = (to: string | number, options?: {state?: unknown}) => void;

type AfterExecuteOpener = (
    result: AiAgentExecuteResult,
    navigate: NavigateFn,
) => void | Promise<void>;

/**
 * A course event holds nothing of its own — what the producer came to define are its courses, so
 * it opens on the page they live in, where the app's own course creation also ends.
 */
function eventReviewRoute({eid, eventType, reviewRoute}: AiAgentExecuteResult): string {
    if(eventType === 'course') {
        return `/e/${eid}/${getTicketsPagePath({type: eventType, eid})}`;
    }
    return reviewRoute || `/e/${eid}/event`;
}

/**
 * After execute, open that record's editor for review.
 */
function openEventWizard(
    result: AiAgentExecuteResult,
    navigate: NavigateFn,
): void {
    const {eid, eventType} = result;
    // The wizard picks its steps from ui.event flags, which changeEvent never clears — left
    // behind, the previous event's flags choose the steps of this one.
    resetAgentEventState();
    if(eventType === 'ticket' || eventType === 'area') {
        setGlobalState('ui.show.isOpenTickchakLoader', true);
    }
    setGlobalState('ui.show.isOpenCreateEvent', false);
    if(eventType === 'scheduled') {
        setGlobalState('ui.event.isScheduled', true);
    }
    // An update opens the event already loaded: same eid, so AppComponent never reloads it.
    const isLoadedEvent = eid === store.getState().event.eid;
    // AppComponent changeEvent resets slices + loads getEvent/permissions.
    navigate(eventReviewRoute(result));
    if(isLoadedEvent) {
        dispatchCustomEvent('changeEvent', {detail: eid});
    }
}

async function openTicketPopup(
    {eid, eventType, tid, reviewRoute}: AiAgentExecuteResult,
    navigate: NavigateFn,
): Promise<void> {
    if(!tid) {
        throw new Error('aiAgent: execute returned CREATE_TICKET without a tid');
    }
    if(eventType === 'subscription') {
        await getPlans();
        setCurrentPlan(tid);
        setGlobalState('ui.show.isOpenCreateSubscription', true);
        navigate(reviewRoute || `/e/${eid}/subscription`);
        return;
    }
    // The page opens the popup on the created ticket, so its list has to hold it first.
    await getEventTickets();
    setGlobalState('aiAgent.pendingTicketTid', tid);
    navigate(reviewRoute || `/e/${eid}/${getTicketsPagePath(store.getState().event)}`);
}

/**
 * Hardcoded on purpose — opening a screen is client code. An intent with no opener falls
 * back to the reviewRoute the server returned, so a new intent still opens with no client
 * change.
 */
const AFTER_EXECUTE_OPENERS: Record<string, AfterExecuteOpener> = {
    CREATE_EVENT: openEventWizard,
    CREATE_TICKET: openTicketPopup,
};

/** Resolves to whether the created record opened — false is "created but not opened", not an error. */
export async function navigateAfterExecute(
    result: AiAgentExecuteResult,
    navigate: NavigateFn,
): Promise<boolean> {
    const openReviewForm: AfterExecuteOpener | undefined = Object.hasOwn(AFTER_EXECUTE_OPENERS, result.intent) ? AFTER_EXECUTE_OPENERS[result.intent] : undefined;
    if(openReviewForm) {
        await openReviewForm(result, navigate);
    } else if(result.reviewRoute) {
        navigate(result.reviewRoute);
    } else {
        return false;
    }
    // On a wide screen the panel stays open beside the record it opened, so its closing turn can
    // ask how the chat went. On a phone it covers the record, so it closes; the question waits
    // in the chat for the next open.
    if(isMobileViewport()) {
        closeAgentPanel();
    }
    return true;
}
